import { useState, useMemo } from "react"
import { motion } from "motion/react"
import { AnimatedNumber } from "@/components/mortgage/AnimatedNumber"
import { useDebounce } from "@/MyCode/hooks/useDebounce"
import JsonFormSteps from "@/MyCode/components/JsonFormSteps"
import type { Step } from "@/MyCode/core/types"
import debounce from "lodash.debounce"
import { renderDollarInputWithFrequency } from "@/MyCode/renderers/web/components/DollarInputWithFrequencyRenderer"

export function DebtToIncomeCalculator() {
    // Income fields
    const [salaryIncome, setSalaryIncome] = useState({ amount: 60000, frequency: 'Year' })
    const [pensionIncome, setPensionIncome] = useState({ amount: 0, frequency: 'Year' })
    const [investmentIncome, setInvestmentIncome] = useState({ amount: 0, frequency: 'Year' })
    const [otherIncome, setOtherIncome] = useState({ amount: 0, frequency: 'Year' })

    // Debt/Expense fields
    const [rentalCost, setRentalCost] = useState({ amount: 1200, frequency: 'Month' })
    const [mortgage, setMortgage] = useState({ amount: 0, frequency: 'Month' })
    const [propertyTax, setPropertyTax] = useState({ amount: 0, frequency: 'Year' })
    const [hoaFees, setHoaFees] = useState({ amount: 0, frequency: 'Month' })
    const [homeownerInsurance, setHomeownerInsurance] = useState({ amount: 0, frequency: 'Year' })
    const [creditCards, setCreditCards] = useState({ amount: 200, frequency: 'Month' })
    const [studentLoan, setStudentLoan] = useState({ amount: 0, frequency: 'Month' })
    const [autoLoan, setAutoLoan] = useState({ amount: 250, frequency: 'Month' })
    const [otherLoans, setOtherLoans] = useState({ amount: 0, frequency: 'Month' })

    // Debounced values
    const debouncedSalaryIncome = useDebounce(salaryIncome, 500)
    const debouncedPensionIncome = useDebounce(pensionIncome, 500)
    const debouncedInvestmentIncome = useDebounce(investmentIncome, 500)
    const debouncedOtherIncome = useDebounce(otherIncome, 500)
    const debouncedRentalCost = useDebounce(rentalCost, 500)
    const debouncedMortgage = useDebounce(mortgage, 500)
    const debouncedPropertyTax = useDebounce(propertyTax, 500)
    const debouncedHoaFees = useDebounce(hoaFees, 500)
    const debouncedHomeownerInsurance = useDebounce(homeownerInsurance, 500)
    const debouncedCreditCards = useDebounce(creditCards, 500)
    const debouncedStudentLoan = useDebounce(studentLoan, 500)
    const debouncedAutoLoan = useDebounce(autoLoan, 500)
    const debouncedOtherLoans = useDebounce(otherLoans, 500)

    // Convert to monthly amounts
    const convertToMonthly = (value: { amount: number; frequency: string }): number => {
        if (value.frequency === 'Year') {
            return value.amount / 12
        }
        return value.amount
    }

    // Calculate total monthly income
    const totalMonthlyIncome = useMemo(() => {
        return (
            convertToMonthly(debouncedSalaryIncome) +
            convertToMonthly(debouncedPensionIncome) +
            convertToMonthly(debouncedInvestmentIncome) +
            convertToMonthly(debouncedOtherIncome)
        )
    }, [debouncedSalaryIncome, debouncedPensionIncome, debouncedInvestmentIncome, debouncedOtherIncome])

    // Calculate total monthly debt/expenses
    const totalMonthlyDebt = useMemo(() => {
        return (
            convertToMonthly(debouncedRentalCost) +
            convertToMonthly(debouncedMortgage) +
            convertToMonthly(debouncedPropertyTax) +
            convertToMonthly(debouncedHoaFees) +
            convertToMonthly(debouncedHomeownerInsurance) +
            convertToMonthly(debouncedCreditCards) +
            convertToMonthly(debouncedStudentLoan) +
            convertToMonthly(debouncedAutoLoan) +
            convertToMonthly(debouncedOtherLoans)
        )
    }, [
        debouncedRentalCost,
        debouncedMortgage,
        debouncedPropertyTax,
        debouncedHoaFees,
        debouncedHomeownerInsurance,
        debouncedCreditCards,
        debouncedStudentLoan,
        debouncedAutoLoan,
        debouncedOtherLoans
    ])

    // Calculate DTI ratio
    const dtiRatio = useMemo(() => {
        if (totalMonthlyIncome <= 0) return 0
        return (totalMonthlyDebt / totalMonthlyIncome) * 100
    }, [totalMonthlyIncome, totalMonthlyDebt])

    // DTI thresholds
    const dtiThresholds = useMemo(() => {
        return {
            safe: 36,      // Safe: ≤36% (green)
            acceptable: 43, // Acceptable: 36-43% (yellow)
            aggressive: 50  // Aggressive: 43-50% (red)
        }
    }, [])

    // Determine DTI status
    const dtiStatus = useMemo(() => {
        if (dtiRatio <= dtiThresholds.safe) {
            return { label: 'Safe', color: 'green' }
        } else if (dtiRatio <= dtiThresholds.acceptable) {
            return { label: 'Acceptable', color: 'yellow' }
        } else if (dtiRatio <= dtiThresholds.aggressive) {
            return { label: 'Aggressive', color: 'red' }
        } else {
            return { label: 'Very High Risk', color: 'red' }
        }
    }, [dtiRatio, dtiThresholds])

    // Helper function to update state from form data
    const updateStateFromFormData = (data: any) => {
        if (data.salaryIncome !== undefined) setSalaryIncome(data.salaryIncome)
        if (data.pensionIncome !== undefined) setPensionIncome(data.pensionIncome)
        if (data.investmentIncome !== undefined) setInvestmentIncome(data.investmentIncome)
        if (data.otherIncome !== undefined) setOtherIncome(data.otherIncome)
        if (data.rentalCost !== undefined) setRentalCost(data.rentalCost)
        if (data.mortgage !== undefined) setMortgage(data.mortgage)
        if (data.propertyTax !== undefined) setPropertyTax(data.propertyTax)
        if (data.hoaFees !== undefined) setHoaFees(data.hoaFees)
        if (data.homeownerInsurance !== undefined) setHomeownerInsurance(data.homeownerInsurance)
        if (data.creditCards !== undefined) setCreditCards(data.creditCards)
        if (data.studentLoan !== undefined) setStudentLoan(data.studentLoan)
        if (data.autoLoan !== undefined) setAutoLoan(data.autoLoan)
        if (data.otherLoans !== undefined) setOtherLoans(data.otherLoans)
    }

    const steps: Step[] = [
        {
            id: "incomes",
            title: "Incomes (Before Tax)",
            description: "Enter your income sources",
            fields: [
                {
                    type: "dollarInputWithFrequency",
                    name: "salaryIncome",
                    label: "Salary & Earned Income",
                    placeholder: "60000",
                    required: false,
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        salaryIncome: 'nullable'
                    }
                },
                {
                    type: "dollarInputWithFrequency",
                    name: "pensionIncome",
                    label: "Pension & Social Security",
                    placeholder: "0",
                    required: false,
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        pensionIncome: 'nullable'
                    }
                },
                {
                    type: "dollarInputWithFrequency",
                    name: "investmentIncome",
                    label: "Investment & Savings",
                    placeholder: "0",
                    required: false,
                    description: "interest, capital gain, dividend, rental income...",
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        investmentIncome: 'nullable'
                    }
                },
                {
                    type: "dollarInputWithFrequency",
                    name: "otherIncome",
                    label: "Other Income",
                    placeholder: "0",
                    required: false,
                    description: "gift, alimony, child support ...",
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        otherIncome: 'nullable'
                    }
                },
            ]
        },
        {
            id: "debts-expenses",
            title: "Debts / Expenses",
            description: "Enter your monthly debts and expenses",
            fields: [
                {
                    type: "dollarInputWithFrequency",
                    name: "rentalCost",
                    label: "Rental Cost",
                    placeholder: "1200",
                    required: false,
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        rentalCost: 'nullable'
                    }
                },
                {
                    type: "dollarInputWithFrequency",
                    name: "mortgage",
                    label: "Mortgage",
                    placeholder: "0",
                    required: false,
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        mortgage: 'nullable'
                    }
                },
                {
                    type: "dollarInputWithFrequency",
                    name: "propertyTax",
                    label: "Property Tax",
                    placeholder: "0",
                    required: false,
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        propertyTax: 'nullable'
                    }
                },
                {
                    type: "dollarInputWithFrequency",
                    name: "hoaFees",
                    label: "HOA Fees",
                    placeholder: "0",
                    required: false,
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        hoaFees: 'nullable'
                    }
                },
                {
                    type: "dollarInputWithFrequency",
                    name: "homeownerInsurance",
                    label: "Homeowner Insurance",
                    placeholder: "0",
                    required: false,
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        homeownerInsurance: 'nullable'
                    }
                },
                {
                    type: "dollarInputWithFrequency",
                    name: "creditCards",
                    label: "Credit Cards",
                    placeholder: "200",
                    required: false,
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        creditCards: 'nullable'
                    }
                },
                {
                    type: "dollarInputWithFrequency",
                    name: "studentLoan",
                    label: "Student Loan",
                    placeholder: "0",
                    required: false,
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        studentLoan: 'nullable'
                    }
                },
                {
                    type: "dollarInputWithFrequency",
                    name: "autoLoan",
                    label: "Auto Loan",
                    placeholder: "250",
                    required: false,
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        autoLoan: 'nullable'
                    }
                },
                {
                    type: "dollarInputWithFrequency",
                    name: "otherLoans",
                    label: "Other Loans and Liabilities",
                    placeholder: "0",
                    required: false,
                    description: "personal loan, child support, alimony, etc.",
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        otherLoans: 'nullable'
                    }
                },
            ]
        }
    ]

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
        >
            <div>
                <h1 className="text-3xl font-bold mb-2">Debt-to-Income Ratio (DTI) Calculator</h1>
                <p className="text-muted-foreground">
                    Calculate your debt-to-income ratio to understand your financial health
                </p>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
                {/* Left Side - Form */}
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1, duration: 0.3 }}
                    className="lg:col-span-3 xl:col-span-2 h-full"
                >
                    <JsonFormSteps
                        divClassName="w-full p-0 h-full"
                        hideSubmitButton={true}
                        steps={steps}
                        onSubmit={(data: any) => {
                            updateStateFromFormData(data)
                        }}
                        onChange={useMemo(
                            () =>
                                debounce((data: any) => {
                                    updateStateFromFormData(data)
                                }, 500),
                            []
                        )}
                        debug={false}
                        debugSingleForm={true}
                        progressBarPosition="top"
                        noCard={false}
                        horizontalLayout={true}
                        initialValues={{
                            salaryIncome: salaryIncome,
                            pensionIncome: pensionIncome,
                            investmentIncome: investmentIncome,
                            otherIncome: otherIncome,
                            rentalCost: rentalCost,
                            mortgage: mortgage,
                            propertyTax: propertyTax,
                            hoaFees: hoaFees,
                            homeownerInsurance: homeownerInsurance,
                            creditCards: creditCards,
                            studentLoan: studentLoan,
                            autoLoan: autoLoan,
                            otherLoans: otherLoans,
                        }}
                        resetOnInitialValuesChange={false}
                        customFieldRenderers={{
                            dollarInputWithFrequency: renderDollarInputWithFrequency
                        }}
                    />
                </motion.div>

                {/* Right Side - Results */}
                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2, duration: 0.3 }}
                    className="lg:col-span-3 xl:col-span-1 space-y-6"
                >
                    {/* Main Result */}
                    <div className="bg-card border border-border rounded-lg p-6">
                        <h2 className="text-xl font-semibold mb-4">Your DTI Ratio</h2>
                        <div className="space-y-4">
                            <div>
                                <p className="text-base text-muted-foreground mb-2">
                                    Debt-to-Income Ratio
                                </p>
                                <p className={`text-4xl font-bold ${dtiStatus.color === 'green' ? 'text-green-600' :
                                    dtiStatus.color === 'yellow' ? 'text-yellow-600' :
                                        'text-red-600'
                                    }`}>
                                    <AnimatedNumber value={dtiRatio} decimals={1} />%
                                </p>
                                <p className={`text-base font-semibold mt-2 ${dtiStatus.color === 'green' ? 'text-green-600' :
                                    dtiStatus.color === 'yellow' ? 'text-yellow-600' :
                                        'text-red-600'
                                    }`}>
                                    {dtiStatus.label}
                                </p>
                            </div>
                        </div>
                    </div>



                    {/* Income Breakdown */}
                    <div className="bg-card border border-border rounded-lg p-6">
                        <h3 className="text-lg font-semibold mb-4">Income Breakdown</h3>
                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Total monthly income:</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={totalMonthlyIncome} decimals={0} />
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Debt Breakdown */}
                    <div className="bg-card border border-border rounded-lg p-6">
                        <h3 className="text-lg font-semibold mb-4">Debt Breakdown</h3>
                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Total monthly debt:</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={totalMonthlyDebt} decimals={0} />
                                </span>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
            {/* DTI Bar Visualization */}
            <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4">DTI Range</h3>
                <div className="space-y-4">
                    {/* Bar visualization */}
                    <div className="relative w-full h-12 bg-secondary rounded-full overflow-hidden">
                        {/* Safe zone (green) */}
                        <div
                            className="absolute left-0 top-0 h-full bg-green-500"
                            style={{
                                width: `${(dtiThresholds.safe / dtiThresholds.aggressive) * 100}%`
                            }}
                        />
                        {/* Acceptable zone (yellow) */}
                        <div
                            className="absolute h-full bg-yellow-500"
                            style={{
                                left: `${(dtiThresholds.safe / dtiThresholds.aggressive) * 100}%`,
                                width: `${((dtiThresholds.acceptable - dtiThresholds.safe) / dtiThresholds.aggressive) * 100}%`
                            }}
                        />
                        {/* Aggressive zone (red) */}
                        <div
                            className="absolute h-full bg-red-500"
                            style={{
                                left: `${(dtiThresholds.acceptable / dtiThresholds.aggressive) * 100}%`,
                                width: `${((dtiThresholds.aggressive - dtiThresholds.acceptable) / dtiThresholds.aggressive) * 100}%`
                            }}
                        />
                        {/* Current DTI indicator */}
                        {dtiRatio > 0 && (
                            <div
                                className="absolute top-0 h-full w-1 bg-black dark:bg-white z-10"
                                style={{
                                    left: `${Math.min((dtiRatio / dtiThresholds.aggressive) * 100, 100)}%`,
                                    transform: 'translateX(-50%)'
                                }}
                            />
                        )}
                    </div>

                    {/* Labels */}
                    <div className="flex justify-between items-center text-sm">
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded bg-green-500"></div>
                            <span className="text-muted-foreground">Safe (≤{dtiThresholds.safe}%)</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded bg-yellow-500"></div>
                            <span className="text-muted-foreground">Acceptable ({dtiThresholds.safe}-{dtiThresholds.acceptable}%)</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded bg-red-500"></div>
                            <span className="text-muted-foreground">Aggressive ({dtiThresholds.acceptable}-{dtiThresholds.aggressive}%)</span>
                        </div>
                    </div>

                    {/* Values */}
                    <div className="space-y-2 pt-2 border-t border-border">
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Safe range:</span>
                            <span className="font-semibold text-green-600">
                                0% - {dtiThresholds.safe}%
                            </span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Acceptable range:</span>
                            <span className="font-semibold text-yellow-600">
                                {dtiThresholds.safe}% - {dtiThresholds.acceptable}%
                            </span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Aggressive range:</span>
                            <span className="font-semibold text-red-600">
                                {dtiThresholds.acceptable}% - {dtiThresholds.aggressive}%
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    )
}

